import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { CustomerApiError, type GroomingCancelPreview, groomingCancelApi } from '../customer_api';
import { formatMoney } from '../lib/format';

interface Props {
  bookingId: string;
  onSuccess: (result: { refundMinor: number; refundSource: string }) => void;
  onClose: () => void;
}

type Phase = 'loading' | 'idle' | 'submitting' | 'error';

/**
 * CancelGroomingDrawer — customer self-serve grooming cancellation (sub-spec 05
 * Plan 5 M2). Loads the cancellation policy preview, then offers a single
 * confirm action that POSTs to /grooming-appointments/:id/cancel.
 *
 * Refund source is always `account_credit` in v1; the drawer surfaces the
 * amount the customer will get back as kennel credit before they commit.
 *
 * Mirrors the sibling `CancelDaycareDrawer` shape so the customer-side UI
 * vocabulary stays consistent across verticals.
 */
export function CancelGroomingDrawer({ bookingId, onSuccess, onClose }: Props) {
  const [phase, setPhase] = useState<Phase>('loading');
  const [preview, setPreview] = useState<GroomingCancelPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    groomingCancelApi
      .getPreview(bookingId)
      .then((p) => {
        if (cancelled) return;
        setPreview(p);
        setPhase('idle');
      })
      .catch((err) => {
        if (cancelled) return;
        const msg =
          err instanceof CustomerApiError && err.status === 422
            ? 'This appointment can no longer be cancelled.'
            : 'Could not load the cancellation preview.';
        setPreviewError(msg);
        setPhase('idle');
      });
    return () => {
      cancelled = true;
    };
  }, [bookingId]);

  const handleConfirm = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (phase === 'submitting') return;
      setPhase('submitting');
      setSubmitError(null);
      try {
        const result = await groomingCancelApi.cancel(bookingId, reason.trim() || undefined);
        onSuccess({
          refundMinor: result.refundMinor,
          refundSource: result.refundSource,
        });
      } catch (err) {
        const msg =
          err instanceof CustomerApiError
            ? err.status === 422
              ? 'This appointment can no longer be cancelled.'
              : `Cancel failed (HTTP ${err.status}). Please try again.`
            : 'Could not cancel the appointment. Please try again.';
        setSubmitError(msg);
        setPhase('idle');
      }
    },
    [bookingId, phase, reason, onSuccess],
  );

  return (
    <div
      style={{
        marginTop: '1.5rem',
        padding: '1.25rem 1.25rem',
        background: 'var(--surface, white)',
        borderRadius: '12px',
        border: '1px solid var(--border, oklch(0.915 0.005 80))',
      }}
      data-testid="grooming-cancel-drawer"
    >
      <div
        style={{
          fontSize: '0.7rem',
          textTransform: 'uppercase',
          letterSpacing: '0.14em',
          color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
          marginBottom: '0.75rem',
        }}
      >
        Cancel this appointment
      </div>

      {phase === 'loading' && (
        <p style={{ color: 'var(--muted-foreground, oklch(0.52 0.008 75))' }}>Loading…</p>
      )}

      {previewError && (
        <p style={{ color: 'var(--destructive, oklch(0.55 0.15 30))', fontSize: '0.875rem' }}>
          {previewError}
        </p>
      )}

      {preview && !previewError && (
        <>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              gap: '1rem',
              padding: '0.3rem 0',
              fontSize: '0.9rem',
            }}
          >
            <span style={{ color: 'var(--muted-foreground, oklch(0.52 0.008 75))' }}>
              Refund as kennel credit
            </span>
            <span className="num" style={{ fontWeight: 500 }} data-testid="cancel-refund-amount">
              {formatMoney(preview.refundMinor, preview.currency)}
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              gap: '1rem',
              padding: '0.3rem 0',
              fontSize: '0.9rem',
            }}
          >
            <span style={{ color: 'var(--muted-foreground, oklch(0.52 0.008 75))' }}>
              Policy refund
            </span>
            <span className="num" style={{ fontWeight: 500 }}>
              {preview.refundPct}%
            </span>
          </div>

          {preview.refundMinor === 0 && (
            <p
              style={{
                marginTop: '0.5rem',
                fontSize: '0.8rem',
                color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
              }}
            >
              The current cancellation policy doesn't include a refund for this date. You can still
              cancel — the appointment will be removed without charge to your account.
            </p>
          )}

          <form
            onSubmit={(e) => void handleConfirm(e)}
            style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}
          >
            <label
              htmlFor="cancel-reason-grooming"
              style={{
                fontSize: '0.7rem',
                textTransform: 'uppercase',
                letterSpacing: '0.14em',
                color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
              }}
            >
              Reason (optional)
            </label>
            <input
              id="cancel-reason-grooming"
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={phase === 'submitting'}
              placeholder="e.g. plans changed"
              data-testid="cancel-reason-input"
              style={{
                padding: '0.5rem 0.75rem',
                borderRadius: '8px',
                border: '1px solid var(--border, oklch(0.915 0.005 80))',
                background: 'var(--background, oklch(0.985 0.003 85))',
                fontSize: '0.9rem',
              }}
            />

            {submitError && (
              <p
                style={{ color: 'var(--destructive, oklch(0.55 0.15 30))', fontSize: '0.85rem' }}
                role="alert"
              >
                {submitError}
              </p>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem' }}>
              <button
                type="button"
                onClick={onClose}
                disabled={phase === 'submitting'}
                style={{
                  padding: '0.5rem 0.85rem',
                  borderRadius: '8px',
                  border: '1px solid var(--border, oklch(0.915 0.005 80))',
                  background: 'transparent',
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                }}
              >
                Keep appointment
              </button>
              <button
                type="submit"
                disabled={phase === 'submitting'}
                data-testid="cancel-confirm"
                style={{
                  padding: '0.5rem 0.85rem',
                  borderRadius: '8px',
                  border: '1px solid var(--destructive, oklch(0.55 0.15 30))',
                  background: 'var(--destructive, oklch(0.55 0.15 30))',
                  color: 'white',
                  fontSize: '0.85rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                {phase === 'submitting' ? 'Cancelling…' : 'Confirm cancellation'}
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );
}
